// In-game HUD. Updated imperatively every frame (no virtual DOM): each module
// owns a few elements and only touches them when their content changes.
// Layout and visibility follow settings.hud; positions keep a safe margin
// from the screen edges and leave the centre of the screen free.

import { bindLabel, keyLabel, type Action, type Binds } from '../../game/binds';
import type { HudConfig } from '../../game/settings';
import type { Vec3 } from '../../shared/math';
import { Cgaz, type CgazState } from './cgaz';
import { ChatLog } from './chatLog';
import { Debug } from './debug';
import { EtjHud, type EtjHudState } from './etjump';
import { el, setText } from './dom';
import { formatTime } from '../../game/settings';
import { Keys } from './keys';
import { Speedometer } from './speedometer';
import { Splits } from './splits';
import { Timer } from './timer';
import { Toasts, type ToastKind } from './toasts';

export type { CgazState } from './cgaz';
export type { EtjHudState } from './etjump';
export * from './keys';

export interface HudState {
  speed: number;
  velocityZ: number;
  health: number;
  /** label replaces the run name in the chip (e.g. "REPLAY"). */
  timer: { run: string; time: number; label?: string } | null;
  fps: number;
  origin: Vec3;
  angles: Vec3;
  noclip: boolean;
  /** KEY_* bits of the held movement keys. */
  keys: number;
  /** Extra debug line (network status). */
  extra?: string;
}

export interface HudOptions {
  showFps: boolean;
  largeText: boolean;
}

/** Controls help text for the current binds. */
export function helpText(b: Binds): string {
  const k = (a: Action) => bindLabel(b, a).toUpperCase();
  // movement: the primary keys only (arrow keys as second set would crowd the line)
  const p = (a: Action) => keyLabel(b[a][0] ?? b[a][1]).toUpperCase();
  return (
    `${p('forward')} ${p('left')} ${p('back')} ${p('right')} move · ${k('jump')} jump · ${k('crouch')} crouch · ${k('prone')} prone · ${k('sprint')} sprint\n` +
    `${k('save')} save · ${k('load')} load · ${k('respawn')} respawn · ${k('noclip')} noclip · ${k('replay')} watch last run · ${k('chat')} chat · ${k('scoreboard')} scores · ${k('help')} help · ESC menu`
  );
}

export class Hud {
  readonly root: HTMLDivElement;
  private readonly timer = new Timer();
  private readonly splits = new Splits();
  private readonly toasts = new Toasts();
  private readonly speed = new Speedometer();
  private readonly keys = new Keys();
  private readonly chatLog = new ChatLog();
  private readonly debug = new Debug();
  private readonly cgaz = new Cgaz();
  private readonly etj = new EtjHud();
  private readonly healthEl = el('div', 'hud-health');
  private readonly healthValue = el('span', 'hud-health-value');
  private readonly fpsEl = el('div', 'hud-fps');
  private readonly helpEl = el('div', 'hud-help');
  private readonly bannerEl = el('div', 'hud-banner');
  private readonly replayEl = el('div', 'hud-replay');
  private readonly replayFill = el('div', 'hud-replay-fill');
  private readonly replayText = el('div', 'hud-replay-text');
  private config: HudConfig | null = null;
  private online = false;
  private noFallDamage = false;
  private helpVisible = true;
  private lastText = 0;
  /** Debug line (draw statistics), shown in the debug block. */
  debugText = '';

  constructor(parent: HTMLElement) {
    this.root = el('div', 'hud');
    this.root.setAttribute('aria-hidden', 'true');
    this.healthEl.append(el('span', 'hud-health-label', 'HP'), this.healthValue);
    const top = el('div', 'hud-top');
    top.append(this.timer.root, this.splits.root, this.toasts.root);
    const track = el('div', 'hud-replay-track');
    track.appendChild(this.replayFill);
    this.replayEl.append(this.replayText, track);
    this.root.append(this.replayEl);
    this.root.append(this.cgaz.canvas, this.etj.snapCanvas, this.etj.root, el('div', 'crosshair'), top, this.speed.root, this.keys.root, this.chatLog.root, this.healthEl, this.debug.root, this.fpsEl, this.helpEl, this.bannerEl);
    parent.appendChild(this.root);
    this.cgaz.readColors();
  }

  /** Applies the HUD settings (module switches, scale, safe margin). */
  configure(cfg: HudConfig, opts: HudOptions): void {
    this.config = cfg;
    const r = this.root;
    r.style.setProperty('--hud-scale', String(cfg.scale * (opts.largeText ? 1.25 : 1)));
    r.style.setProperty('--hud-margin', String(cfg.safeMargin));
    r.classList.toggle('no-timer', !cfg.timer);
    r.classList.toggle('no-speed', !cfg.speed);
    r.classList.toggle('no-keys', !cfg.keys);
    r.classList.toggle('no-splits', !cfg.splits);
    r.classList.toggle('no-debug', !cfg.debug);
    r.classList.toggle('no-fps', !opts.showFps);
    r.classList.toggle('show-vz', cfg.verticalSpeed);
    this.speed.colorize = cfg.speedColor;
    this.cgaz.canvas.style.display = cfg.cgaz ? '' : 'none';
    this.etj.snapCanvas.style.display = cfg.snaphud ? '' : 'none';
    this.chatLog.root.style.display = this.online && cfg.chat ? '' : 'none';
    this.cgaz.readColors();
  }

  /** Map dependent behaviour: online widgets, health hidden at 100 on no-fall-damage maps. */
  setContext(ctx: { online: boolean; noFallDamage: boolean }): void {
    this.online = ctx.online;
    this.noFallDamage = ctx.noFallDamage;
    if (!ctx.online) this.chatLog.clear();
    this.chatLog.root.style.display = ctx.online && (this.config?.chat ?? true) ? '' : 'none';
  }

  resize(width: number, height: number): void {
    this.cgaz.resize(width, height);
    this.etj.resize(width, height);
  }

  /** The ETJump training HUDs; modules switched off in the settings get null. */
  drawEtj(state: EtjHudState): void {
    const c = this.config;
    this.etj.update(
      {
        ob: c?.obDetector ? state.ob : null,
        jumps: c?.jumpSpeeds ? state.jumps : null,
        snap: c?.snaphud ? state.snap : null,
        upmove: c?.upmoveMeter ? state.upmove : null,
        strafeQuality: c?.strafeQuality ? state.strafeQuality : null,
        maxSpeed: c?.maxSpeed ? state.maxSpeed : null,
      },
      performance.now(),
    );
  }

  drawCgaz(state: CgazState | null): void {
    if (this.config && !this.config.cgaz) return;
    this.cgaz.draw(state);
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? '' : 'none';
  }

  showHelp(v: boolean): void {
    this.helpVisible = v;
    this.helpEl.style.display = v ? '' : 'none';
  }

  get helpShown(): boolean {
    return this.helpVisible;
  }

  setHelpText(text: string): void {
    this.helpEl.textContent = text;
  }

  /** The timeline of a watched replay; null hides it. */
  setReplay(r: { time: number; total: number; speed: number; paused: boolean; label: string } | null): void {
    this.replayEl.classList.toggle('visible', !!r);
    if (!r) return;
    this.replayFill.style.transform = `scaleX(${(r.total ? Math.min(1, r.time / r.total) : 0).toFixed(4)})`;
    setText(this.replayText, `${r.paused ? '❚❚' : '▶'} ${formatTime(r.time)} / ${formatTime(r.total)}${r.speed !== 1 ? ` · ${r.speed}×` : ''}`);
  }

  /** Centre banner (e.g. while watching a replay); null hides it. */
  setBanner(text: string | null): void {
    this.bannerEl.textContent = text ?? '';
    this.bannerEl.classList.toggle('visible', !!text);
  }

  update(s: HudState): void {
    // speed, timer and keys every frame, the rest at ~10 Hz
    this.timer.update(s.timer);
    this.speed.update(s.speed, s.velocityZ);
    this.keys.update(s.keys);
    const now = performance.now();
    if (now - this.lastText < 100) return;
    this.lastText = now;
    const hp = Math.max(0, Math.round(s.health));
    setText(this.healthValue, String(hp));
    const hideHealth = !(this.config?.health ?? true) || (this.noFallDamage && hp >= 100);
    this.healthEl.classList.toggle('hidden', hideHealth);
    this.healthEl.classList.toggle('low', hp <= 30);
    setText(this.fpsEl, `${Math.round(s.fps)} FPS`);
    if (this.config?.debug) this.debug.update({ fps: s.fps, origin: s.origin, angles: s.angles, velocityZ: s.velocityZ, noclip: s.noclip, draw: this.debugText, extra: s.extra });
  }

  /** Run-event toast under the timer. */
  message(text: string, kind?: ToastKind, duration?: number): void {
    this.toasts.show(text, kind, duration);
  }

  /** Checkpoint split: its time in the run. */
  split(label: string, time: number): void {
    if (this.config && !this.config.splits) return;
    this.splits.show(label, time);
  }

  clearRunInfo(): void {
    this.splits.clear();
  }

  chat(from: string | null, text: string): void {
    this.chatLog.add(from, text);
  }

  get chatting(): boolean {
    return this.chatLog.open;
  }

  openChat(done: (text: string | null) => void): void {
    // offline the log is hidden: the field still opens for /commands
    const hidden = this.chatLog.root.style.display === 'none';
    if (hidden) this.chatLog.root.style.display = '';
    this.chatLog.openField((text) => {
      if (hidden) this.chatLog.root.style.display = 'none';
      done(text);
    });
  }

  closeChat(text: string | null = null): void {
    this.chatLog.close(text);
  }
}
